import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useRobot } from "@/context/RobotContext";
import ConnectionPanel from "@/components/ConnectionPanel";
import CameraFeed from "@/components/CameraFeed";
import CommandCenter from "@/components/CommandCenter";
import ActivityLog from "@/components/ActivityLog";
import Telemetry from "@/components/Telemetry";
import InventoryTable from "@/components/InventoryTable";
import DriveMode from "@/components/DriveMode";
import { Robot, SignOut, GameController } from "@phosphor-icons/react";

const statusColor = {
  connected: "#00E676",
  pairing: "#FFB000",
  disconnected: "#FF3B30",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { status } = useRobot();
  const [driveOpen, setDriveOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#0A0B0E]">
      {/* Top bar */}
      <header className="border-b border-[#272A35] bg-[#111318] sticky top-0 z-40">
        <div className="w-full max-w-screen-2xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Robot size={26} weight="bold" className="text-[#FF5A00] shrink-0" />
            <div className="min-w-0">
              <h1 className="font-head font-black text-lg tracking-tight leading-none">MECHALOG</h1>
              <p className="text-[9px] tracking-[0.25em] uppercase text-[#475569] truncate">robot control terminal</p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <button
              data-testid="drive-mode-btn"
              onClick={() => setDriveOpen(true)}
              className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[#FF5A00] border border-[#FF5A00] hover:bg-[#FF5A00] hover:text-[#0A0B0E] px-3 py-1.5 transition-colors active:scale-95"
            >
              <GameController size={15} weight="bold" /> <span className="hidden sm:inline">Drive</span>
            </button>
            <span className="hidden sm:flex items-center gap-2 text-xs uppercase font-bold border border-[#272A35] px-3 py-1.5" style={{ color: statusColor[status] }} data-testid="header-status">
              <span className="w-2 h-2 rounded-full" style={{ background: statusColor[status], boxShadow: `0 0 8px ${statusColor[status]}` }} />
              {status}
            </span>
            <span className="hidden md:block text-xs text-[#94A3B8]">{user?.name || user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#94A3B8] hover:text-[#FF3B30] border border-[#272A35] hover:border-[#FF3B30] px-3 py-1.5 transition-colors"
            >
              <SignOut size={14} weight="bold" /> Exit
            </button>
          </div>
        </div>
      </header>

      {/* Grid */}
      <main className="w-full max-w-screen-2xl mx-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        <div className="lg:col-span-8 fade-up">
          <CameraFeed />
        </div>
        <div className="lg:col-span-4 fade-up" style={{ animationDelay: "60ms" }}>
          <ConnectionPanel />
        </div>

        <div className="lg:col-span-8 fade-up" style={{ animationDelay: "120ms" }}>
          <CommandCenter />
        </div>
        <div className="lg:col-span-4 fade-up" style={{ animationDelay: "180ms" }}>
          <ActivityLog />
        </div>

        <div className="lg:col-span-12 fade-up" style={{ animationDelay: "210ms" }}>
          <Telemetry />
        </div>

        <div className="lg:col-span-12 fade-up" style={{ animationDelay: "240ms" }}>
          <InventoryTable />
        </div>
      </main>

      {driveOpen && <DriveMode onClose={() => setDriveOpen(false)} />}
    </div>
  );
}
